import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Link as LinkIcon, Loader2, Paperclip } from "lucide-react";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { useDocumentFormats } from "@/hooks/useDocumentFormats";
import { useDownloadFile } from "@/hooks/useDownloadFile";
import { useDisplayDocument } from "@/hooks/useDisplayDocument";
import { useReplaceCustomInvoice } from "@/hooks/useReplaceCustomInvoice";
import {
  ExpeditionDocumentFileDto,
  GetCustomV1BoResponse,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { DocumentsUpdateModal } from "@/components/shared/modal/documentsUpdate.modal";
import { BO_FINALIZE_EXPEDITION } from "@/constants/fileCrud";
import { ExpeditionDocumentCard } from "../../expeditionDocumentCard";
import { SingleDocumentUploadCard } from "../../singleDocumentUploadCard";
import { LinkFileModal } from "./linkFileModal";

export const FileContent = ({
  expedition,
  custom,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  custom: GetCustomV1BoResponse;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const documents = expedition.expeditionDocuments?.documents ?? [];
  const apiLinkedDocuments = documents
    .filter((d) => d.sendViaApi)
    .filter(
      (d) =>
        d.type !== FileTypeEnum.EXPEDITION_CUSTOM_INVOICE &&
        d.type !== FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL &&
        d.type !== FileTypeEnum.EXPEDITION_SHIPPING_LABEL &&
        d.type !== FileTypeEnum.EXPEDITION_FILES_TO_PRINT,
    );

  const [linkFileModalOpen, setLinkFileModalOpen] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const { downloadFile } = useDownloadFile();
  const { displayDocument } = useDisplayDocument();
  const { displayFile, alternateFormats } = useDocumentFormats(
    documents,
    FileTypeEnum.EXPEDITION_CUSTOM_INVOICE,
  );
  const { replaceCustomInvoice, isReplacing } = useReplaceCustomInvoice(
    expedition.expeditionDocuments,
    expedition.id,
  );

  const handleDownload = (file: ExpeditionDocumentFileDto) => downloadFile(file.path, file.name);

  const handldeDownloadAll = () => {
    setIsDownloadingAll(true);
    const customInvoice = documents.find((d) => d.type === FileTypeEnum.EXPEDITION_CUSTOM_INVOICE);
    if (customInvoice) {
      downloadFile(customInvoice.path, customInvoice.name);
    }
    apiLinkedDocuments?.forEach((document) => {
      downloadFile(document.path, document.name);
    });
    setIsDownloadingAll(false);
  };

  return (
    <AccordionItem
      value="fileContent"
      className="shadow-basic flex w-full max-w-[1400px] flex-col items-start justify-center gap-4 rounded-xl border border-gray-200 bg-slate-100 p-6"
    >
      <AccordionTrigger className="flex w-full flex-row items-start justify-between gap-2 self-start !no-underline">
        <div className="flex flex-row items-center gap-2 self-start">
          <Paperclip className="h-6 w-6 !rotate-0 text-blue-600" />
          <p className="text-xl font-bold">{t("fileContent.title")}</p>
        </div>
      </AccordionTrigger>
      <AccordionContent forceMount className="flex w-full flex-col gap-4">
        {
          // If the destination is UE there should not be any custom invoice as it is not required
          custom.region === "EUROPEANUNION" &&
          !documents.find((d) => d.type === FileTypeEnum.EXPEDITION_CUSTOM_INVOICE) ? (
            <></>
          ) : (
            <div className="flex w-full flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
              <SingleDocumentUploadCard
                file={displayFile}
                alternateFormats={alternateFormats}
                emptyMessage={t("fileContent.customInvoiceEmpty")}
                onOpen={displayDocument}
                onDownload={handleDownload}
                onFileSelect={replaceCustomInvoice}
                isUploading={isReplacing}
                accept=".pdf"
              />
            </div>
          )
        }
        <div className="flex w-full flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
          {apiLinkedDocuments?.map((document) => (
            <ExpeditionDocumentCard key={document.id} file={document} />
          ))}
          <Button
            type="button"
            variant="ghost"
            className="w-fit self-center text-blue-800"
            onClick={() => setLinkFileModalOpen(true)}
          >
            <LinkIcon className="mr-2 size-4" />
            {t("fileContent.manageApiFiles")}
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-fit self-start"
          onClick={handldeDownloadAll}
          disabled={isDownloadingAll}
          aria-label={t("fileContent.downloadAll")}
          title={t("fileContent.downloadAll")}
        >
          {isDownloadingAll ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
        </Button>
        <LinkFileModal
          open={linkFileModalOpen}
          onClose={() => setLinkFileModalOpen(false)}
          expedition={expedition}
        />
      </AccordionContent>
    </AccordionItem>
  );
};

export const FileContentWoApi = ({
  expedition,
  custom,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  custom: GetCustomV1BoResponse;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const documents = expedition.expeditionDocuments?.documents ?? [];
  const filteredDocuments = documents.filter(
    (d) =>
      d.type !== FileTypeEnum.EXPEDITION_CUSTOM_INVOICE &&
      d.type !== FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL &&
      d.type !== FileTypeEnum.EXPEDITION_SHIPPING_LABEL &&
      d.type !== FileTypeEnum.EXPEDITION_FILES_TO_PRINT,
  );

  const [linkFileModalOpen, setLinkFileModalOpen] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const { downloadFile } = useDownloadFile();
  const { displayDocument } = useDisplayDocument();
  const { displayFile, alternateFormats } = useDocumentFormats(
    documents,
    FileTypeEnum.EXPEDITION_CUSTOM_INVOICE,
  );
  const { replaceCustomInvoice, isReplacing } = useReplaceCustomInvoice(
    expedition.expeditionDocuments,
    expedition.id,
  );

  const handleDownload = (file: ExpeditionDocumentFileDto) => downloadFile(file.path, file.name);

  const handldeDownloadAll = () => {
    setIsDownloadingAll(true);
    const customInvoice = documents.find((d) => d.type === FileTypeEnum.EXPEDITION_CUSTOM_INVOICE);
    if (customInvoice) {
      downloadFile(customInvoice.path, customInvoice.name);
    }
    filteredDocuments?.forEach((document) => {
      downloadFile(document.path, document.name);
    });
    setIsDownloadingAll(false);
  };

  return (
    <AccordionItem
      value="fileContent"
      className="shadow-basic flex w-full max-w-[1400px] flex-col items-start justify-center gap-4 rounded-xl border border-gray-200 bg-slate-100 p-6"
    >
      <AccordionTrigger className="flex w-full flex-row items-start justify-between gap-2 self-start !no-underline">
        <div className="flex flex-row items-center gap-2 self-start">
          <Paperclip className="h-6 w-6 !rotate-0 text-blue-600" />
          <p className="text-xl font-bold">{t("fileContent.titleWoApi")}</p>
        </div>
      </AccordionTrigger>
      <AccordionContent forceMount className="flex w-full flex-col gap-4">
        {
          // If the destination is UE there should not be any custom invoice as it is not required
          custom.region === "EUROPEANUNION" &&
          !documents.find((d) => d.type === FileTypeEnum.EXPEDITION_CUSTOM_INVOICE) ? (
            <></>
          ) : (
            <div className="flex w-full flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
              <SingleDocumentUploadCard
                file={displayFile}
                alternateFormats={alternateFormats}
                emptyMessage={t("fileContent.customInvoiceEmpty")}
                onOpen={displayDocument}
                onDownload={handleDownload}
                onFileSelect={replaceCustomInvoice}
                isUploading={isReplacing}
                accept=".pdf"
              />
            </div>
          )
        }
        <div className="flex w-full flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
          {filteredDocuments?.map((document) => (
            <ExpeditionDocumentCard key={document.id} file={document} />
          ))}
          <Button
            type="button"
            variant="ghost"
            className="w-fit self-center text-blue-800"
            onClick={() => setLinkFileModalOpen(true)}
          >
            {t("fileContent.manageFiles")}
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-fit self-start"
          onClick={handldeDownloadAll}
          disabled={isDownloadingAll}
          aria-label={t("fileContent.downloadAll")}
          title={t("fileContent.downloadAll")}
        >
          {isDownloadingAll ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
        </Button>
        <DocumentsUpdateModal
          open={linkFileModalOpen}
          onClose={() => setLinkFileModalOpen(false)}
          expeditionId={expedition.id}
          documents={filteredDocuments}
          expeditionDocumentsId={expedition.expeditionDocuments?.id}
          invalidateQueryKey={["expedition", "bo", expedition.id]}
          isBo
          expedition={expedition}
          fileContextRules={BO_FINALIZE_EXPEDITION}
        />
      </AccordionContent>
    </AccordionItem>
  );
};
